// You can write more code here

/* START OF COMPILED CODE */

import BaseScene from '../core/scenes/BaseScene';
/* START-USER-IMPORTS */
import assetPack from '../../../static/assets/asset-pack-game.json';
import { app } from '../core/App';
import { GameSceneData, SceneData, SceneId, TransitionPoint } from './../rooting';
import GameUI from './GameUI';
import { GameDataHandler } from '../GameDataHandler';
import { LevelManager } from './../level/LevelManager';
import { World, WorldName, WorldNameValues } from '../level/worlds/world';
import { ParallaxController } from '../core/modules/Parallax/ParallaxController';
import { RunManager } from './../level/RunManager';
import { MatterPhysicsUpdater } from '../core/modules/MatterPhysicsUpdater';
import { testWorld } from '../level/worlds/test';
import { getWorldFromId } from '../level/worlds';
import { RunSettings, RunSettingsHandler } from '../level/RunSettingsHandler';
import { RelicName } from './../level/relics/relic';
/* END-USER-IMPORTS */

export default class Game extends BaseScene {
  constructor() {
    super('Game');

    /* START-USER-CTR-CODE */
    this.runManager = new RunManager(this, false, false);
    this.levelManager = new LevelManager(this, testWorld);
    /* END-USER-CTR-CODE */
  }

  editorCreate(): void {
    // BackgroundLayer
    const backgroundLayer = this.add.layer();

    // ChunkBackgroundLayer
    const chunkBackgroundLayer = this.add.layer();

    // PlayerLayer
    const playerLayer = this.add.layer();

    // ChunkForegroundLayer
    const chunkForegroundLayer = this.add.layer();

    // PlayerDeathLayer
    const playerDeathLayer = this.add.layer();

    this.backgroundLayer = backgroundLayer;
    this.chunkBackgroundLayer = chunkBackgroundLayer;
    this.playerLayer = playerLayer;
    this.chunkForegroundLayer = chunkForegroundLayer;
    this.playerDeathLayer = playerDeathLayer;

    this.events.emit('scene-awake');
  }

  private backgroundLayer!: Phaser.GameObjects.Layer;
  private chunkBackgroundLayer!: Phaser.GameObjects.Layer;
  private playerLayer!: Phaser.GameObjects.Layer;
  private chunkForegroundLayer!: Phaser.GameObjects.Layer;
  private playerDeathLayer!: Phaser.GameObjects.Layer;

  /* START-USER-CODE */
  public sceneData!: SceneData;
  private gameSceneData!: GameSceneData;
  private runSettings!: RunSettings;
  private gameUI!: GameUI;
  private physicsUpdater!: MatterPhysicsUpdater;
  private runManager!: RunManager;
  private levelManager!: LevelManager;
  private parallaxController!: ParallaxController;
  // private player!: Player;

  public init(data: SceneData) {
    let gameSceneDataDefaults: GameSceneData = {
      shouldSkip: false
    };

    let runSettingsDefaults: RunSettings = {
      startWorldId: WorldName.Shipwreck,
      instantRestart: false,
      regenOnRestart: true,
      shouldSpawnChunksForCollectedRelics: false,
      activeRelics: [],
      maxAirJumps: 1,
      glideTime: 0,
      startingContinueTokens: 0,
      canBreakAllSmashables: false,
      magnetStrength: 0,
      startingGlobalSpeed: 1,
      maxGlobalSpeed: 1.4
    };

    this.sceneData = data;
    this.gameSceneData = Object.assign({}, gameSceneDataDefaults, data.gameSceneData);
    this.runSettings = Object.assign({}, runSettingsDefaults, data.runSettings);

    this.registry.set(GameDataHandler.keys.Score, 0);
    this.matter.world.autoUpdate = false;
  }

  public preload() {
    this.load.pack('asset-pack-game', assetPack as any);

    this.load.once(Phaser.Loader.Events.COMPLETE, this.postload, this);
  }

  public postload() {
    super.postload();
  }

  public create() {
    this.load.off(Phaser.Loader.Events.COMPLETE, this.postload, this);
    this.editorCreate();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
    this.events.once(Phaser.Scenes.Events.DESTROY, this.destroy, this);

    this.playerDeathLayer.setDepth(100);

    this.showGameUI();

    this.setupGame();

    let firstWorld = getWorldFromId(this.runSettings.startWorldId);

    this.runManager.initRun(this.runSettings, this.cameras.main, this.physicsUpdater, this.playerLayer, this.playerDeathLayer, firstWorld);
    this.startGame();

    if (this.gameSceneData.shouldSkip) this.time.delayedCall(100, () => this.runOver());
  }

  public shutdown() {
    if (this?.gameUI?.scene?.isActive()) this.gameUI.scene.stop();

    this.events.off(Phaser.Scenes.Events.DESTROY, this.destroy, this);
    this.gameUI.events.off(Phaser.Scenes.Events.CREATE, this.gameUI.showUI, this.gameUI);

    this.events.off(RunManager.events.RunOver, this.runOver, this);
    this.events.off(RunManager.events.WorldSwitchStarted, this.showWorldTransitionCover, this);
    this.events.off(RunManager.events.WorldSwitchEnded, this.hideWorldTransitionCover, this);
    this.events.off(RunManager.events.ShowMessage, this.showMessage, this);
    this.events.off(RunManager.events.HideMessage, this.hideMessage, this);

    this?.parallaxController?.clearLayers();

    super.shutdown();
  }

  public destroy() {
    super.destroy();
  }

  public pause() {
    this.scene.pause();
  }

  public resume() {
    this.scene.resume();
  }

  public back() {
    this.runManager.Player?.freeze();
    app.gotoNextScene(this, TransitionPoint.GameBack, this.sceneData);
  }

  protected runOver() {
    this.runManager.Player?.freeze();
    this.finish();
  }

  protected finish() {
    this.gameUI.hideUI();

    let score = this.registry.get(GameDataHandler.keys.Score);
    let highScore = this.registry.get(GameDataHandler.keys.HighScore);

    this.gameUI.showAudioBtn();
    this.gameUI.showEndScreen(score, highScore, this.runSettings.activeRelics);

    if (score > highScore) this.registry.set(GameDataHandler.keys.HighScore, score);
  }

  protected showGameUI() {
    this.scene.run(SceneId.GameUI);
    this.gameUI = this.scene.get(SceneId.GameUI) as GameUI;
    this.gameUI.events.once(Phaser.Scenes.Events.CREATE, this.gameUI.showUI, this.gameUI);
  }

  // One time setup for the scene.
  protected setupGame() {
    this.matter.world.setGravity(0, 1, 0.002);

    this.cameras.main.setZoom(0.8, 0.8);
    // this.cameras.main.setZoom(1, 1);

    this.physicsUpdater = new MatterPhysicsUpdater(this, 1000 / 60, 2);
    this.parallaxController = new ParallaxController(this, this.cameras.main, this.backgroundLayer);

    this.levelManager.init(this.chunkBackgroundLayer, this.chunkForegroundLayer);
    this.runManager.init(this.levelManager, this.parallaxController);

    this.events.on(RunManager.events.RunOver, this.runOver, this);
    this.events.on(RunManager.events.WorldSwitchStarted, this.showWorldTransitionCover, this);
    this.events.on(RunManager.events.WorldSwitchEnded, this.hideWorldTransitionCover, this);
    this.events.on(RunManager.events.ShowMessage, this.showMessage, this);
    this.events.on(RunManager.events.HideMessage, this.hideMessage, this);
  }

  protected startGame() {
    this.time.delayedCall(1000, () => this.runManager.startRun());
  }

  protected showMessage(message: string) {
    this?.gameUI?.showMessage(message);
  }

  protected hideMessage() {
    this?.gameUI?.hideMessage();
  }

  protected showWorldTransitionCover(worldId: WorldNameValues) {
    this.gameUI.showWorldTransition(worldId);
  }

  protected hideWorldTransitionCover(worldId: WorldNameValues) {
    this.gameUI.hideWorldTransition(worldId);
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */
